/**
 * WindowLayer — mounts all open floating windows + the minimized dock.
 * Renders at a high z-index root but with NO backdrop, so the main
 * dashboard stays fully interactive underneath.
 */
import React from "react";
import { Maximize2, X } from "lucide-react";
import { useFloatingWindows } from "@/lib/floatingWindows";
import { setPresenceSection } from "@/lib/presence";
import FloatingWindow from "@/components/nds/windows/FloatingWindow";
import { CardAccessGate } from "@/components/nds/windows/CardAccessGate";
import CompactWindow from "@/components/nds/windows/CompactWindow";
import { useCompactLayout } from "@/lib/compactLayout";
import QuickEditContent from "@/components/nds/windows/QuickEditContent";
import QuickFormsContent from "@/components/ops/QuickFormsContent";
import CardConfigContent from "@/components/ops/CardConfigContent";
import StepCoachingContent from "@/components/nds/windows/StepCoachingContent";
import {
  BriefIntakeContent,
  NewContactContent,
  SendSmsContent,
  OpenModuleContent,
} from "@/components/nds/windows/ActionContent";
import CampusResidentContent from "@/components/nds/windows/CampusResidentContent";
import RePortalAsContent from "@/components/nds/windows/RePortalAsContent";
import ResidentIntakeContent from "@/components/nds/windows/ResidentIntakeContent";
import Pillar1InnerContent from "@/components/nds/windows/Pillar1InnerContent";
import Pillar2VocationContent from "@/components/nds/windows/Pillar2VocationContent";
import Pillar3SpiritContent from "@/components/nds/windows/Pillar3SpiritContent";
import Pillar4DailyContent from "@/components/nds/windows/Pillar4DailyContent";
import FinLedgerContent from "@/components/nds/windows/FinLedgerContent";
import FinPaycheckContent from "@/components/nds/windows/FinPaycheckContent";
import PaycheckFormWindow from "@/components/nds/windows/finance/PaycheckFormWindow";
import FinSavingsContent from "@/components/nds/windows/FinSavingsContent";
import FinPenaltiesContent from "@/components/nds/windows/FinPenaltiesContent";
import FinInvoicingContent from "@/components/nds/windows/FinInvoicingContent";
import FinSessionPayoutsContent from "@/components/nds/windows/FinSessionPayoutsContent";
import FinRentRollContent from "@/components/nds/windows/FinRentRollContent";
import FinProgramFeesContent from "@/components/nds/windows/FinProgramFeesContent";
import FinStaffExpensesContent from "@/components/nds/windows/FinStaffExpensesContent";
import FinDirectorCreditsContent from "@/components/nds/windows/FinDirectorCreditsContent";
import FinFundRequestsContent from "@/components/nds/windows/FinFundRequestsContent";
import FinPayrollContent from "@/components/nds/windows/FinPayrollContent";
import FinClearinghouseContent from "@/components/nds/windows/FinClearinghouseContent";
import FinAgencyDistributionsContent from "@/components/nds/windows/FinAgencyDistributionsContent";
import FinFlowContent from "@/components/nds/windows/finance/flow/FinFlowContent";
import FinRecapContent from "@/components/nds/windows/FinRecapContent";
import FinBankLedgerContent from "@/components/nds/windows/FinBankLedgerContent";
import FinLandlordPayablesContent from "@/components/nds/windows/FinLandlordPayablesContent";
import FinOrgLedgerContent from "@/components/nds/windows/FinOrgLedgerContent";
import ParDirectoryContent from "@/components/nds/windows/ParDirectoryContent";
import ReIntakeFormContent from "@/components/nds/windows/ReIntakeFormContent";
import ReCheckinsContent from "@/components/nds/windows/ReCheckinsContent";
import ReDocumentsContent from "@/components/nds/windows/ReDocumentsContent";
import ReConductContent from "@/components/nds/windows/ReConductContent";
import ReCaseNotesContent from "@/components/nds/windows/ReCaseNotesContent";
import ReMemosContent from "@/components/nds/windows/ReMemosContent";
import PartnerMemosContent from "@/components/nds/windows/partner/memos/PartnerMemosContent";
import ResidentActionsContent from "@/components/nds/windows/finance/ResidentActionsContent";
import PaycheckReviewContent from "@/components/nds/windows/finance/PaycheckReviewContent";
import ReSaControlsContent from "@/components/nds/windows/ReSaControlsContent";
import RePropertiesContent from "@/components/nds/windows/RePropertiesContent";
import ReRoomAssignmentsContent from "@/components/nds/windows/ReRoomAssignmentsContent";
import ReIncidentsContent from "@/components/nds/windows/ReIncidentsContent";
import SmsContent from "@/components/nds/windows/sms/SmsContent";
import ParAssignmentsContent from "@/components/nds/windows/ParAssignmentsContent";
import ParReferralsContent from "@/components/nds/windows/ParReferralsContent";
import ParSponsorshipsContent from "@/components/nds/windows/ParSponsorshipsContent";
import ParProgramStatsContent from "@/components/nds/windows/ParProgramStatsContent";
import DocTemplateLibraryContent from "@/components/nds/windows/DocTemplateLibraryContent";
import DocGeneratorContent from "@/components/nds/windows/DocGeneratorContent";
import DocAssignmentsContent from "@/components/nds/windows/DocAssignmentsContent";
import DocSignaturesContent from "@/components/nds/windows/DocSignaturesContent";
import DocVaultContent from "@/components/nds/windows/DocVaultContent";
import LifePassContent from "@/components/nds/windows/LifePassContent";
import PillarBoardContent from "@/components/nds/windows/pillar/PillarBoardContent";
import SchCalendarContent from "@/components/nds/windows/SchCalendarContent";
import { SchEventDetailWindow, SchEventFormWindow, SchTaskFormWindow } from "@/components/nds/windows/sch/SchWindows";
import SchConflictsContent from "@/components/nds/windows/SchConflictsContent";
import OpsProblemsContent from "@/components/nds/windows/OpsProblemsContent";
import FixDuplicateContent from "@/components/nds/windows/FixDuplicateContent";
import FixGuidedContent from "@/components/nds/windows/FixGuidedContent";
import DocsCenterContent from "@/components/nds/windows/DocsCenterContent";
import DocsFieldsContent from "@/components/nds/windows/DocsFieldsContent";
import SchAuditContent from "@/components/nds/windows/SchAuditContent";
import SchAvailabilityContent from "@/components/nds/windows/SchAvailabilityContent";
import SessionContent from "@/components/nds/windows/session/SessionContent";
import { usePublishWindows } from "@/lib/allyContext";
import { GrantReportContent, GrantTileContent } from "@/components/nds/grants/GrantWindows";

export const KIND_MAP = {
  quickEdit:         { Content: QuickEditContent,     kicker: "// QUICK EDIT · SCHEMA" },
  brief_intake:      { Content: BriefIntakeContent,   kicker: "// NEW FRICTION AUDIT BRIEF" },
  new_contact:       { Content: NewContactContent,    kicker: "// NEW CRM CONTACT" },
  send_sms:          { Content: SendSmsContent,       kicker: "// SEND SMS" },
  open_module:       { Content: OpenModuleContent,    kicker: "// OPEN MODULE" },
  step_coaching:     { Content: StepCoachingContent,  kicker: "// STEP COACHING" },
  quickForms:        { Content: QuickFormsContent,    kicker: "// QUICK ENTRY" },
  cardConfig:        { Content: CardConfigContent,    kicker: "// CONFIGURE CARD" },
  campus_residents:  { Content: CampusResidentContent, kicker: "// CAMPUS · RESIDENT ROSTER" },
  re_portal_as:      { Content: RePortalAsContent, kicker: "// RESIDENT ENGINE · RESIDENT PORTAL" },
  resident_intake:   { Content: ResidentIntakeContent, kicker: "// RESIDENT ENGINE · INTAKE" },
  pillar_board:      { Content: PillarBoardContent,     kicker: "// PILLAR ENGINE · BOARD" },
  pillar_1_inner:    { Content: Pillar1InnerContent,    kicker: "// PILLAR 1 · INNER STRENGTH" },
  pillar_2_vocation: { Content: Pillar2VocationContent, kicker: "// PILLAR 2 · VOCATION" },
  pillar_3_spirit:   { Content: Pillar3SpiritContent,   kicker: "// PILLAR 3 · SPIRIT" },
  pillar_4_daily:    { Content: Pillar4DailyContent,    kicker: "// PILLAR 4 · DAILY TOUCHPOINT" },
  fin_ledger:        { Content: FinLedgerContent,       kicker: "// FINANCE · RESIDENT LEDGER" },
  fin_paychecks:     { Content: FinPaycheckContent,     kicker: "// FINANCE · PAYCHECK INTAKE" },
  fin_paycheck_form: { Content: PaycheckFormWindow,     kicker: "// FINANCE · LOG PAYCHECK" },
  fin_savings:       { Content: FinSavingsContent,      kicker: "// FINANCE · SAVINGS" },
  fin_penalties:     { Content: FinPenaltiesContent,    kicker: "// FINANCE · PENALTIES" },
  fin_invoicing:     { Content: FinInvoicingContent,    kicker: "// FINANCE · INVOICING" },
  fin_session_payouts: { Content: FinSessionPayoutsContent, kicker: "// FINANCE · SESSION PAYOUTS" },
  fin_rent_roll:     { Content: FinRentRollContent,     kicker: "// FINANCE · RENT ROLL" },
  fin_program_fees:  { Content: FinProgramFeesContent,  kicker: "// FINANCE · PROGRAM FEES" },
  fin_staff_expenses: { Content: FinStaffExpensesContent, kicker: "// FINANCE · STAFF EXPENSES" },
  fin_director_credits: { Content: FinDirectorCreditsContent, kicker: "// FINANCE · DIRECTOR CREDITS" },
  fin_fund_requests: { Content: FinFundRequestsContent, kicker: "// FINANCE · FUND REQUESTS" },
  fin_payroll: { Content: FinPayrollContent, kicker: "// FINANCE · PAYROLL" },
  fin_clearinghouse: { Content: FinClearinghouseContent, kicker: "// FINANCE · CLEARINGHOUSE" },
  fin_agency_distributions: { Content: FinAgencyDistributionsContent, kicker: "// FINANCE · AGENCY DISTRIBUTIONS" },
  fin_reports: { Content: FinFlowContent, kicker: "// FINANCE · REPORT" },
  fin_recap: { Content: FinRecapContent, kicker: "// FINANCE · RECAP" },
  fin_bank_ledger: { Content: FinBankLedgerContent, kicker: "// FINANCE · MANUAL BANK LEDGER" },
  fin_landlord_payables: { Content: FinLandlordPayablesContent, kicker: "// FINANCE · INVOICE FUNDING & LANDLORD PAYABLES" },
  fin_org_ledger: { Content: FinOrgLedgerContent, kicker: "// FINANCE · TEA ORGANIZATION LEDGER" },
  par_directory: { Content: ParDirectoryContent, kicker: "// PARTNER · DIRECTORY" },
  re_intake_form: { Content: ReIntakeFormContent, kicker: "// RESIDENT · INTAKE FORM" },
  re_checkins: { Content: ReCheckinsContent, kicker: "// RESIDENT · WEEKLY CHECK-IN" },
  re_session: { Content: SessionContent, kicker: "// RESIDENT · SIT-DOWN SESSION" },
  re_documents: { Content: ReDocumentsContent, kicker: "// DOCUMENT · RESIDENT FILES" },
  re_conduct: { Content: ReConductContent, kicker: "// RESIDENT · CONDUCT" },
  re_case_notes: { Content: ReCaseNotesContent, kicker: "// RESIDENT · CASE NOTES" },
  re_lifepass: { Content: LifePassContent, kicker: "// RESIDENT · PACT LIFE PASS" },
  re_memos: { Content: ReMemosContent, kicker: "// RESIDENT · MEMO BOARD" },
  par_memos: { Content: PartnerMemosContent, kicker: "// PARTNER · MEMOS" },
  fin_resident_actions: { Content: ResidentActionsContent, kicker: "// FINANCE · RESIDENT ACTIONS" },
  fin_paycheck_review: { Content: PaycheckReviewContent, kicker: "// FINANCE · PAYCHECK REVIEW" },
  fix_duplicate: { Content: FixDuplicateContent, kicker: "// FIX IT WITH ME · DUPLICATE PAYCHECK" },
  fix_guided: { Content: FixGuidedContent, kicker: "// FIX IT WITH ME" },
  docs_center: { Content: DocsCenterContent, kicker: "// DOCUMENTS" },
  docs_fields: { Content: DocsFieldsContent, kicker: "// DOCUMENTS · SET UP BLANKS" },
  ops_problems: { Content: OpsProblemsContent, kicker: "// OPERATIONS · PROBLEMS INBOX" },
  re_sa_controls: { Content: ReSaControlsContent, kicker: "// RESIDENT · SA CONTROLS" },
  re_properties: { Content: RePropertiesContent, kicker: "// RESIDENT · PROPERTIES" },
  re_room_assignments: { Content: ReRoomAssignmentsContent, kicker: "// RESIDENT · ROOM ASSIGNMENTS" },
  re_incidents: { Content: ReIncidentsContent, kicker: "// RESIDENT · INCIDENT REPORTS" },
  sms_panel: { Content: SmsContent, kicker: "// SMS · WHO, WHEN AND WHAT WAS SENT" },
  par_assignments: { Content: ParAssignmentsContent, kicker: "// PARTNER · ASSIGNMENTS" },
  par_referrals: { Content: ParReferralsContent, kicker: "// PARTNER · REFERRALS" },
  par_sponsorships: { Content: ParSponsorshipsContent, kicker: "// PARTNER · SPONSORSHIPS" },
  par_program_stats: { Content: ParProgramStatsContent, kicker: "// PARTNER · PROGRAM STATS" },
  doc_template_library: { Content: DocTemplateLibraryContent, kicker: "// DOCUMENT · TEMPLATE LIBRARY" },
  doc_generator: { Content: DocGeneratorContent, kicker: "// DOCUMENT · GENERATOR" },
  doc_assignments: { Content: DocAssignmentsContent, kicker: "// DOCUMENT · ASSIGNMENTS" },
  doc_signatures: { Content: DocSignaturesContent, kicker: "// DOCUMENT · SIGNATURES" },
  doc_vault: { Content: DocVaultContent, kicker: "// DOCUMENT · VAULT" },
  sch_calendar: { Content: SchCalendarContent, kicker: "// SCHEDULER · CALENDAR" },
  sch_event_form: { Content: SchEventFormWindow, kicker: "// SCHEDULER · EVENT" },
  sch_event_detail: { Content: SchEventDetailWindow, kicker: "// SCHEDULER · EVENT DETAIL" },
  sch_task_form: { Content: SchTaskFormWindow, kicker: "// SCHEDULER · DAY TASK" },
  sch_conflicts: { Content: SchConflictsContent, kicker: "// SCHEDULER · CONFLICTS & OVERRIDES" },
  sch_audit: { Content: SchAuditContent, kicker: "// SCHEDULER · AUDIT LOG" },
  sch_availability: { Content: SchAvailabilityContent, kicker: "// SCHEDULER · AVAILABILITY" },
  grant_report: { Content: GrantReportContent, kicker: "// GRANTS · FUNDER REPORT" },
  grant_tile: { Content: GrantTileContent, kicker: "// GRANTS · FUNDER OUTCOMES" },
};

export const titleFor = (win) => {
  if (win.kind === "quickEdit") {
    const kindLabel = { kpi: "KPI Card", step: "Revenue Step", capability: "Capability Tile" }[win.payload?.kind] || win.payload?.kind;
    return `Edit · ${kindLabel}`;
  }
  if (win.kind === "step_coaching") {
    return `Step ${win.payload?.step?.id || ""} · ${win.payload?.step?.title || ""}`;
  }
  return win.payload?.title || KIND_MAP[win.kind]?.kicker || "Window";
};

export const subtitleFor = (win) => {
  if (win.kind === "quickEdit") return `${win.payload?.verticalId || ""} · ${win.payload?.itemId || ""}`;
  return win.payload?.description || null;
};

/**
 * popoutHrefFor — maps a window to its dedicated full-page URL so the user
 * can "pop out" a popup into its own browser tab. Returns null when there
 * is no matching standalone page (button then hides).
 */
export const popoutHrefFor = (win) => {
  switch (win.kind) {
    case "brief_intake":  return "/new-brief";                      // full-page Friction Audit
    case "new_contact":   return "/admin/contacts";                 // CRM contacts list — new form inline
    case "send_sms":      return "/admin/sms";                      // SMS console
    case "open_module":   return win.payload?.href || null;
    case "quickForms":    return win.payload?.href || null;
    case "cardConfig":    return "/admin/forms";
    case "quickEdit":     return "/admin/nds";                      // full JSON schema editor
    case "step_coaching": return `/admin/briefs?step=${encodeURIComponent(win.payload?.step?.id || "")}`;
    default:              return KIND_MAP[win.kind] ? null : win.payload?.href || null;
  }
};

export default function WindowLayer({ baseTitle = "Desktop" }) {
  const { windows, restoreWindow, closeWindow } = useFloatingWindows();
  const { compact, panel } = useCompactLayout();
  const open = windows.filter((w) => !w.minimized);
  const minimized = windows.filter((w) => w.minimized);
  usePublishWindows(open.map((w) => ({ id: w.id, kind: w.kind, title: titleFor(w), z: w.z, payload: w.payload })));
  const topTitle = open.length ? titleFor(open.reduce((a, b) => (b.z > a.z ? b : a))) : baseTitle;
  React.useEffect(() => { setPresenceSection(topTitle); }, [topTitle]);

  if (compact) {
    if (!windows.length) return null;
    const top = windows.reduce((a, b) => (b.z > a.z ? b : a));
    const entry = KIND_MAP[top.kind] || KIND_MAP.open_module;
    const { Content } = entry;
    return (
      <CompactWindow key={top.id} win={top} windows={windows} panel={panel} kicker={entry.kicker} title={titleFor(top)}
        subtitle={subtitleFor(top)} popoutHref={popoutHrefFor(top)} titleFor={titleFor}>
        <CardAccessGate kind={top.kind}><Content win={top} /></CardAccessGate>
      </CompactWindow>
    );
  }

  return (
    <>
      {/* Floating windows layer — pointer-events-none root so board underneath
          stays interactive; individual windows re-enable pointer events. */}
      <div className="fixed inset-0 pointer-events-none z-[45]" data-testid="nds-window-layer">
        {open.map((win) => {
          // Kinds without a built window yet (e.g. upcoming fin_* cards) fall back to the deep-link module.
          const entry = KIND_MAP[win.kind] || KIND_MAP.open_module;
          const { Content } = entry;
          return (
            <FloatingWindow
              key={win.id}
              win={win}
              kicker={entry.kicker}
              title={titleFor(win)}
              subtitle={subtitleFor(win)}
              popoutHref={popoutHrefFor(win)}
            >
              <CardAccessGate kind={win.kind}><Content win={win} /></CardAccessGate>
            </FloatingWindow>
          );
        })}
      </div>

      {/* Minimized dock */}
      {minimized.length > 0 && (
        <div
          className="fixed bottom-3 left-1/2 -translate-x-1/2 z-[46] flex items-center gap-2 px-3 py-2 bg-[#0B1220] border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.6)]"
          data-testid="nds-window-dock"
        >
          <span className="font-mono text-[0.55rem] tracking-[0.22em] text-nex-muted uppercase">Minimized</span>
          {minimized.map((win) => (
            <div
              key={win.id}
              className="inline-flex items-center gap-1 pl-2 pr-1 border border-white/10 hover:border-nex-gold/50 transition-colors"
              data-testid={`nds-window-dock-item-${win.kind}`}
            >
              <button
                type="button"
                onClick={() => restoreWindow(win.id)}
                className="inline-flex items-center gap-1.5 py-1 text-nex-silver hover:text-nex-gold font-mono text-[0.6rem] tracking-[0.14em] uppercase max-w-[180px] truncate"
                title={titleFor(win)}
              >
                <Maximize2 className="w-3 h-3" /> {titleFor(win)}
              </button>
              <button
                type="button"
                onClick={() => closeWindow(win.id)}
                className="w-5 h-5 grid place-items-center text-nex-muted hover:text-nex-silver"
                aria-label="Close"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
